import { createFileRoute, useNavigate } from '@tanstack/react-router';
import { useMemo, useState } from 'react';
import { addDays, endOfMonth, isSameMonth, startOfMonth } from 'date-fns';
import { enUS, ptBR } from 'date-fns/locale';
import { useTranslation } from 'react-i18next';
import { trpc, type RouterOutputs } from '@org/clients/school';
import {
  ProgrammeCalendar,
  Skeleton,
  extractFirstUrl,
  stripUrlFromLocation,
  programmeClassInks,
  programmeCommunityInkPairs,
  programmeTutorInks,
  type ProgrammeInkPair,
  type ProgrammeCalendarEvent,
  type ProgrammeCalendarLabels,
} from '@org/ui';

export const Route = createFileRoute('/_app/calendar')({
  component: CalendarPage,
});

type StudentCalendarEvent = RouterOutputs['events']['getStudentCalendarEvents'][number];

const CLASS_EVENT_KINDS = new Set(['group-class', 'private-class']);

/** Shared class-material links surfaced as buttons in the calendar rail. */
const CLASS_NOTES_URL =
  'https://docs.google.com/document/d/1CtP-QfgQzBf2fhvJsU0nriIkCcmdfwrZGvb-RbEtfJc/edit?usp=sharing';
const CLASS_RECORDINGS_URL =
  'https://drive.google.com/drive/folders/18hnFHt-_YU3jd6M3brNGKf0MSv1FkX9L?usp=sharing';

/** Community events are keyed by category; uncategorized ones share a bucket. */
const communityKindKey = (event: StudentCalendarEvent) =>
  event.category ? `cat:${event.category.eventCategoryId}` : 'community';

function CalendarPage() {
  const { t, i18n } = useTranslation('p2e');
  const navigate = useNavigate();
  const [month, setMonth] = useState(() => startOfMonth(new Date()));

  const dateFnsLocale = i18n.language?.startsWith('pt') ? ptBR : enUS;

  // Pad the window by a week on each side so outside days of the grid are
  // populated too. Dates go over the wire as UTC ISO strings (project rule).
  const rangeStart = useMemo(() => addDays(startOfMonth(month), -7), [month]);
  const rangeEnd = useMemo(() => addDays(endOfMonth(month), 7), [month]);

  const { data, isLoading, isError } = trpc.events.getStudentCalendarEvents.useQuery(
    {
      rangeStart: rangeStart.toISOString(),
      rangeEnd: rangeEnd.toISOString(),
    },
    { staleTime: 60_000 },
  );

  const events = useMemo<ProgrammeCalendarEvent[]>(() => {
    const source = data ?? [];

    // Deterministic community-category ink pairs: palette assigned by
    // first-appearance order (chronological) within the displayed month, so
    // colors are stable for a given month. Padded out-of-month events pick up
    // follow-on inks after the visible ones.
    const communityInksByKey = new Map<string, ProgrammeInkPair>();
    const assignInks = (key: string) => {
      let inks = communityInksByKey.get(key);
      if (!inks) {
        inks = programmeCommunityInkPairs[communityInksByKey.size % programmeCommunityInkPairs.length];
        communityInksByKey.set(key, inks);
      }
      return inks;
    };
    const chronological = source
      .slice()
      .sort((a, b) => new Date(a.startTime).getTime() - new Date(b.startTime).getTime());
    for (const event of chronological) {
      if (CLASS_EVENT_KINDS.has(event.eventKind) || event.eventKind === 'tutoring') continue;
      if (isSameMonth(new Date(event.startTime), month)) assignInks(communityKindKey(event));
    }

    return source.map((event) => {
      let kind: string;
      let kindLabel: string;
      let kindInks: ProgrammeInkPair;
      if (CLASS_EVENT_KINDS.has(event.eventKind)) {
        kind = 'class';
        kindLabel = t('calendar.programme.kindClass');
        kindInks = programmeClassInks;
      } else if (event.eventKind === 'tutoring') {
        kind = 'tutor';
        kindLabel = t('calendar.programme.kindTutor');
        kindInks = programmeTutorInks;
      } else {
        kind = communityKindKey(event);
        kindLabel = event.category?.name ?? t('calendar.programme.kindCommunity');
        kindInks = assignInks(kind);
      }
      return {
        eventId: event.eventId,
        title: event.title,
        startTime: event.startTime,
        endTime: event.endTime,
        kind,
        kindLabel,
        kindInk: kindInks.dot,
        kindLabelInk: kindInks.label,
        hostName: event.hostName,
        hostAvatarUrl: event.hostAvatarUrl,
        locationText: stripUrlFromLocation(event.location),
        joinUrl: extractFirstUrl(event.location),
        classId: event.classId,
      };
    });
  }, [data, t, month]);

  const labels = useMemo<ProgrammeCalendarLabels>(
    () => ({
      eyebrow: t('calendar.programme.eyebrow'),
      title: t('calendar.programme.title'),
      prevMonth: t('calendar.programme.prevMonth'),
      nextMonth: t('calendar.programme.nextMonth'),
      today: t('calendar.programme.today'),
      todayFlag: t('calendar.programme.todayFlag'),
      thisWeek: t('calendar.programme.thisWeek'),
      joinNow: t('calendar.programme.joinNow'),
      join: t('calendar.programme.join'),
      joinWindowNote: t('calendar.programme.joinWindowNote'),
      goToClass: t('calendar.programme.goToClass'),
      close: t('calendar.programme.close'),
      scrollUp: (count: number) => t('calendar.programme.scrollUp', { count }),
      backToToday: t('calendar.programme.backToToday'),
      atAGlance: (monthName: string) => t('calendar.programme.atAGlance', { month: monthName }),
      startsIn: (minutes: number) => t('calendar.programme.startsIn', { minutes }),
      happeningNow: t('calendar.programme.happeningNow'),
      entrySub: (host: string) => t('calendar.programme.entrySub', { host }),
      with: t('calendar.programme.with'),
      where: t('calendar.programme.where'),
      legendTitle: t('calendar.programme.legendTitle'),
      legendNote: t('calendar.programme.legendNote'),
      resourcesTitle: t('calendar.programme.resourcesTitle'),
      monthSectionAria: t('calendar.programme.monthSectionAria'),
      quoteAria: t('calendar.programme.quoteAria'),
    }),
    [t],
  );

  const interlude = useMemo(
    () => ({
      quote: t('calendar.programme.interludeQuote'),
      // bell hooks styled her name lowercase; the i18n value stays lowercase
      // and the design's `.z-who` uppercasing is a visual treatment only.
      attribution: t('calendar.programme.interludeWho'),
    }),
    [t],
  );

  if (isLoading && !data) {
    return (
      <div className="space-y-3">
        <Skeleton className="h-8 w-56" />
        <Skeleton className="h-[420px] w-full rounded-2xl" />
      </div>
    );
  }

  if (isError) {
    return <p className="py-12 text-center text-sm text-gray-500">{t('calendar.error')}</p>;
  }

  // The design supplies its own paper background and 880px page gutters, so
  // it renders full-page — no card wrapper around it.
  return (
    <ProgrammeCalendar
      month={month}
      onMonthChange={(date) => setMonth(startOfMonth(date))}
      events={events}
      labels={labels}
      locale={dateFnsLocale}
      interlude={interlude}
      legendKindOrder={['class', 'tutor']}
      resourceLinks={[
        { label: t('calendar.programme.classNotes'), href: CLASS_NOTES_URL },
        { label: t('calendar.programme.classRecordings'), href: CLASS_RECORDINGS_URL },
      ]}
      onGoToClass={(event) => {
        if (!event.classId) return;
        navigate({ to: '/classes/$classId', params: { classId: event.classId } });
      }}
    />
  );
}
